// src/components/forms/variants/embedded/EmbeddedEmailCapture.tsx

'use client'

import { forwardRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import type { RenderProps, ContentConfig } from '~/components/forms/base/types'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import { EMBEDDED_PRESETS } from '~/components/forms/variants/embedded/types'
import { siteConfig } from '~/lib/config/site'

export const EmbeddedEmailCapture = forwardRef<HTMLFormElement, EmbeddedEmailCaptureProps>(({
  preset = 'banner',
  theme: customTheme,
  content: customContent,
  template: customTemplate,
  ...props
}, ref) => {
  // Memoize merged configurations
  const theme = useMemo(() => ({
    ...EMBEDDED_PRESETS[preset]?.theme,
    ...customTheme,
    className: {
      ...EMBEDDED_PRESETS[preset]?.theme?.className,
      ...customTheme?.className
    }
  }), [preset, customTheme])

  const content = useMemo(() => {
    const presetContent = EMBEDDED_PRESETS[preset]?.content
    const defaultContent: ContentConfig = {
      button: {
        text: 'Subscribe',
        loadingText: 'Processing...'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Please enter a valid email address.'
      }
    }

    const mergedContent = {
      ...defaultContent,
      ...presetContent,
      ...customContent,
      button: {
        ...defaultContent.button,
        ...presetContent?.button,
        ...customContent?.button
      },
      input: {
        placeholder: 'Enter your email',
        ...presetContent?.input,
        ...customContent?.input
      },
      messages: {
        ...defaultContent.messages,
        ...presetContent?.messages,
        ...customContent?.messages
      }
    }

    // Only add privacy if not explicitly disabled
    if (presetContent?.privacy?.enabled !== false && customContent?.privacy?.enabled !== false) {
      mergedContent.privacy = {
        enabled: true,
        text: 'By continuing, you agree to our',
        ...presetContent?.privacy,
        ...customContent?.privacy,
        privacyLink: (presetContent?.privacy?.privacyLink ?? customContent?.privacy?.privacyLink) ? {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`,
          ...presetContent?.privacy?.privacyLink,
          ...customContent?.privacy?.privacyLink
        } : undefined,
        termsLink: (presetContent?.privacy?.termsLink ?? customContent?.privacy?.termsLink) ? {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`,
          ...presetContent?.privacy?.termsLink,
          ...customContent?.privacy?.termsLink
        } : undefined
      }
    }

    return mergedContent
  }, [preset, customContent])

  // Configure email template
  const template = useMemo(() => ({
    templateAlias: customTemplate?.templateAlias ?? 'welcome',
    to: '', // This will be set by BaseEmailCaptureForm
    from: customTemplate?.from ?? process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
    templateModel: {
      base: {
        name: '',  // Will be set with email prefix
        logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? 'https://www.adsights.ai/logo-square.png',
        product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? 'AdSights',
        product_url: process.env.NEXT_PUBLIC_PRODUCT_URL ?? 'https://www.adsights.ai',
        support_email: process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? 'support@adsights.ai',
        sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? 'The AdSights Team',
        company_name: process.env.NEXT_PUBLIC_COMPANY_NAME ?? 'AdSights Inc.',
        company_address: process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
        ...customTemplate?.templateModel?.base
      },
      custom: customTemplate?.templateModel?.custom
    },
    ...customTemplate,  // Spread other custom template properties last
  }), [customTemplate])

  // Memoize the form render function
  const renderForm = ({ state, handlers }: RenderProps): JSX.Element => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={cn(
          preset === 'banner' && "px-4 py-6 md:px-8 md:py-10",
          preset === 'card' && "p-6 sm:p-8",
          preset === 'inline' && "p-6",
          preset === 'indigo' && "p-6",
          theme.className?.container
        )}
      >
        <div className={cn(
          "flex flex-col gap-6",
          preset === 'banner' && "md:flex-row md:items-start md:gap-12",
          preset === 'card' && "items-center text-center",
          preset === 'inline' && "gap-4",
          preset === 'indigo' && "gap-4"
        )}>
          {/* Header */}
          <div className={cn(
            preset === 'banner' && "flex-1 max-w-xl space-y-2 md:space-y-3",
            preset === 'card' && "w-full max-w-md space-y-2.5",
            preset === 'inline' && "w-full space-y-2",
            preset === 'indigo' && "space-y-1"
          )}>
            {content.title && (
              <h4 className={cn(
                "font-semibold tracking-tight",
                preset === 'banner' && "text-xl md:text-2xl lg:text-3xl text-gray-900 dark:text-white",
                preset === 'card' && "text-xl text-gray-900 dark:text-white",
                preset === 'inline' && "text-lg text-gray-900 dark:text-white",
                preset === 'indigo' && "text-base text-indigo-900 dark:text-indigo-100",
                content.title.className
              )}>
                {content.title.text}
              </h4>
            )}
            {content.description && (
              <p className={cn(
                preset === 'banner' && "text-base md:text-lg leading-relaxed text-gray-600 dark:text-gray-300",
                preset === 'card' && "text-base text-gray-600 dark:text-gray-300",
                preset === 'inline' && "text-base text-gray-600 dark:text-gray-300",
                preset === 'indigo' && "text-sm text-indigo-700 dark:text-indigo-300",
                content.description.className
              )}>
                {content.description.text}
              </p>
            )}
          </div>

          {/* Form */}
          <motion.div 
            className={cn(
              "flex flex-col",
              preset === 'banner' && "flex-1 w-full md:max-w-xl",
              preset === 'card' && "w-full max-w-md",
              preset === 'inline' && "w-full",
              preset === 'indigo' && "w-full"
            )}
          >
            <div className={cn(
              "relative flex flex-col gap-3",
              preset === 'banner' && "md:flex-row md:gap-4",
              preset === 'card' && "sm:flex-row sm:gap-3",
              preset === 'inline' && "sm:flex-row sm:gap-3",
              preset === 'indigo' && "sm:flex-row sm:gap-3"
            )}>
              <div className="flex-1 min-w-0">
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  type="email"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={content.input?.placeholder ?? "Enter your email"}
                  className={cn(
                    "block w-full transition duration-200",
                    error && "ring-red-300 dark:ring-red-500/30 focus:ring-red-500",
                    content.input?.className
                  )}
                  disabled={isSubmitting || isSuccess}
                  aria-invalid={!!error}
                  aria-describedby={error ? "email-error" : undefined}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                aria-label={isSubmitting ? content.button?.loadingText : content.button?.text}
                onClick={async (e) => {
                  e.preventDefault()
                  try {
                    await handleSubmit(e)
                  } catch (_err) {
                    // Error is already handled by useEmailForm
                    return
                  }
                }}
                className={cn(
                  'inline-flex items-center justify-center gap-1',
                  'px-4 py-2.5 text-sm font-medium rounded-lg',
                  'bg-indigo-600 text-white',
                  'hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                  'disabled:opacity-50 dark:disabled:opacity-40',
                  'transition-all duration-150 ease-in-out',
                  'transform hover:scale-[1.02] active:scale-[0.98]',
                  'max-w-[300px]',
                  content.button?.className
                )}
              >
                {isSuccess && !error ? (
                  <div className="flex items-center gap-2 truncate">
                    <svg className="h-5 w-5 flex-shrink-0 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5 }}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="truncate">{content.messages?.success}</span>
                  </div>
                ) : (
                  <>
                    <span className="truncate">{isSubmitting ? content.button?.loadingText : content.button?.text}</span>
                    {content.button?.icon}
                  </>
                )}
              </button>
            </div>

            {/* Messages Container - Only show error here, success is shown in button */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.2 }}
                  className={cn(
                    preset === 'banner' && "sm:text-left",
                    preset === 'card' && "text-center",
                    preset === 'inline' && "text-left",
                    preset === 'indigo' && "mt-3"
                  )}
                >
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                      <span className="h-2 w-2 rounded-full bg-red-600 dark:bg-red-400" />
                    </span>
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Privacy Notice */}
            {content.privacy?.enabled !== false && (
              <motion.div
                layout
                className={cn(
                  "text-xs",
                  preset === 'banner' && "sm:text-left mt-3",
                  preset === 'card' && "text-center mt-3",
                  preset === 'inline' && "text-left mt-3",
                  preset === 'indigo' && "mt-3",
                  preset === 'indigo' ? "text-indigo-600 dark:text-indigo-400" : "text-gray-500 dark:text-gray-400",
                  content.privacy?.className
                )}
              >
                <p className="leading-relaxed">
                  {content.privacy?.text}{' '}
                  {content.privacy?.privacyLink && (
                    <a 
                      href={content.privacy.privacyLink.url}
                      className={cn(
                        "font-medium transition-colors",
                        preset === 'indigo' 
                          ? "text-indigo-700 hover:text-indigo-600 dark:text-indigo-300 dark:hover:text-indigo-200"
                          : "text-gray-900 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200"
                      )}
                    >
                      {content.privacy.privacyLink.text}
                    </a>
                  )}
                  {content.privacy?.termsLink && (
                    <>
                      {' and '}
                      <a 
                        href={content.privacy.termsLink.url}
                        className={cn(
                          "font-medium transition-colors",
                          preset === 'indigo' 
                            ? "text-indigo-700 hover:text-indigo-600 dark:text-indigo-300 dark:hover:text-indigo-200"
                            : "text-gray-900 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200"
                        )}
                      >
                        {content.privacy.termsLink.text}
                      </a>
                    </>
                  )}
                  .
                </p>
                {content.privacy?.disclaimer && (
                  <p className={cn(
                    "mt-1.5",
                    preset === 'indigo' ? "text-indigo-500 dark:text-indigo-400" : "text-gray-400 dark:text-gray-500"
                  )}>
                    {content.privacy.disclaimer}
                  </p>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.div>
    )
  }

  return (
    <BaseEmailCaptureForm
      ref={ref}
      theme={theme}
      content={content}
      template={template}
      {...props}
    >
      {renderForm}
    </BaseEmailCaptureForm>
  )
})

EmbeddedEmailCapture.displayName = 'EmbeddedEmailCapture' 